import { ApiResponse, PaginatedResponse } from "../types/common";
import {
  Teacher,
  TeacherWithUserInfo,
  CreateTeacherRequest,
  UpdateTeacherRequest,
  TeacherFilter,
  TeacherStatistics,
} from "../types/teacher";
import api from "./api";

// Teacher CRUD Operations
export const getTeachers = async (
  filters: TeacherFilter = {}
): Promise<PaginatedResponse<TeacherWithUserInfo>> => {
  try {
    const params = new URLSearchParams();

    if (filters.page) params.append("page", filters.page.toString());
    if (filters.limit) params.append("limit", filters.limit.toString());
    if (filters.search) params.append("search", filters.search);
    if (filters.department) params.append("department", filters.department);
    if (filters.isActive !== undefined)
      params.append("isActive", filters.isActive.toString());
    if (filters.status) params.append("status", filters.status);

    const response = await api.get(`/teachers?${params.toString()}`);

    // Transform API response structure from backend format to frontend format
    if (response && (response as any).teachers) {
      return {
        success: (response as any).success,
        data: (response as any).teachers,
        pagination: {
          currentPage: (response as any).currentPage,
          totalPages: (response as any).totalPages,
          totalItems: (response as any).total,
          hasNext: (response as any).currentPage < (response as any).totalPages,
          hasPrev: (response as any).currentPage > 1,
        },
      };
    }

    return response as unknown as PaginatedResponse<TeacherWithUserInfo>;
  } catch (error) {
    console.error("Error fetching teachers:", error);
    // Return a default structure for error cases
    return {
      success: false,
      data: [],
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        hasNext: false,
        hasPrev: false,
      },
    };
  }
};

export const getTeacherById = async (
  id: string
): Promise<ApiResponse<TeacherWithUserInfo>> => {
  try {
    const response = await api.get(`/teachers/${id}`);
    // Backend returns { success: true, teacher: {...} }
    return {
      success: (response as any).success,
      data: (response as any).teacher,
      message: (response as any).message,
    };
  } catch (error) {
    console.error("Error fetching teacher by ID:", error);
    return {
      success: false,
      data: {} as TeacherWithUserInfo,
      message: "Failed to fetch teacher",
    };
  }
};

export const getMyTeacherProfile = async (): Promise<
  ApiResponse<TeacherWithUserInfo>
> => {
  try {
    const response = await api.get(`/teachers/me`);
    // Backend returns { success: true, teacher: {...} }
    return {
      success: (response as any).success,
      data: (response as any).teacher, // Changed from data to teacher
      message: (response as any).message,
    };
  } catch (error) {
    console.error("Error fetching my teacher profile:", error);
    return {
      success: false,
      data: {} as TeacherWithUserInfo,
      message: "Failed to fetch teacher profile",
    };
  }
};

export const createTeacher = async (
  data: CreateTeacherRequest
): Promise<ApiResponse<Teacher>> => {
  try {
    const response = await api.post("/teachers", data);
    return response as unknown as any;
  } catch (error: any) {
    console.error("Error creating teacher:", error);
    throw error; // Re-throw to handle in component
  }
};

export const updateTeacher = async (
  id: string,
  data: UpdateTeacherRequest
): Promise<ApiResponse<Teacher>> => {
  try {
    const response = await api.put(`/teachers/${id}`, data);
    return response as unknown as any;
  } catch (error: any) {
    console.error("Error updating teacher:", error);
    throw error; // Re-throw to handle in component
  }
};

export const deleteTeacher = async (id: string): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete(`/teachers/${id}`);
    return response as unknown as any;
  } catch (error) {
    console.error("Error deleting teacher:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to delete teacher",
    };
  }
};

// Teacher Statistics
export const getTeacherStats = async (
  branchId?: string,
  filters?: { startDate?: string; endDate?: string }
): Promise<ApiResponse<TeacherStatistics>> => {
  try {
    const params = new URLSearchParams();
    if (branchId) params.append("branchId", branchId);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);

    const queryString = params.toString() ? `?${params.toString()}` : "";
    const response = await api.get(`/teachers/statistics${queryString}`);

    // Response is already the data (due to axios interceptor), not response.data
    // Transform API response structure from { success, statistics } to { success, data }
    if (response && (response as any).statistics) {
      const transformedData = {
        success: (response as any).success,
        data: (response as any).statistics,
      };
      return transformedData;
    }

    return response as unknown as ApiResponse<TeacherStatistics>;
  } catch (error) {
    console.error("Error fetching teacher statistics:", error);
    // Return fallback data
    const fallbackData = {
      success: true,
      data: {
        totalTeachers: 0,
        activeTeachers: 0,
        inactiveTeachers: 0,
        averageExperience: 0,
        teachersByDepartment: {},
        teachersByStatus: {},
      },
    };
    return fallbackData;
  }
};

// Bulk Operations
export const bulkUpdateTeachers = async (
  ids: string[],
  data: Partial<UpdateTeacherRequest>
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.put("/teachers/bulk", { ids, data });
    return response as unknown as any;
  } catch (error) {
    console.error("Error bulk updating teachers:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to bulk update teachers",
    };
  }
};

export const bulkDeleteTeachers = async (
  ids: string[]
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.delete("/teachers/bulk", { data: { ids } });
    return response as unknown as any;
  } catch (error) {
    console.error("Error bulk deleting teachers:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to bulk delete teachers",
    };
  }
};

// Additional Teacher Operations
export const assignTeacherToCourse = async (
  teacherId: string,
  courseId: string
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post(`/teachers/${teacherId}/assign-course`, {
      courseId,
    });
    return response as unknown as any;
  } catch (error) {
    console.error("Error assigning teacher to course:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to assign teacher to course",
    };
  }
};

export const unassignTeacherFromCourse = async (
  teacherId: string,
  courseId: string
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post(`/teachers/${teacherId}/unassign-course`, {
      courseId,
    });
    return response as unknown as any;
  } catch (error) {
    console.error("Error unassigning teacher from course:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to unassign teacher from course",
    };
  }
};

export const assignTeacherToClass = async (
  teacherId: string,
  classId: string
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post(`/teachers/${teacherId}/assign-class`, {
      classId,
    });
    return response as unknown as any;
  } catch (error) {
    console.error("Error assigning teacher to class:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to assign teacher to class",
    };
  }
};

export const unassignTeacherFromClass = async (
  teacherId: string,
  classId: string
): Promise<ApiResponse<void>> => {
  try {
    const response = await api.post(`/teachers/${teacherId}/unassign-class`, {
      classId,
    });
    return response as unknown as any;
  } catch (error) {
    console.error("Error unassigning teacher from class:", error);
    return {
      success: false,
      data: undefined as any,
      message: "Failed to unassign teacher from class",
    };
  }
};

// Legacy functions for backward compatibility
export const fetchTeachers = async (): Promise<Teacher[]> => {
  const response = await getTeachers();
  return response as unknown as any;
};

export const fetchTeacherById = async (id: string): Promise<Teacher> => {
  const response = await getTeacherById(id);
  return response as unknown as any;
};
